import dayjs, { Dayjs, ConfigType } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import React from 'react'
import { useStorageSettingsSelect } from './useStorageSettings'

dayjs.extend(utc)
dayjs.extend(timezone)

export const useDateTime = () => {
  const timeZone = useStorageSettingsSelect.timeZone()

  const getForSave = React.useCallback(
    (
      input?: ConfigType,
      format?: string,
      skipTimezone?: boolean
    ): string | null => {
      if (!input) return null

      if (skipTimezone) {
        const date = dayjs(input)

        // @ts-ignore
        if (!date.isValid()) return input

        return date.format(format || 'YYYY-MM-DD')
      }

      if (!timeZone) return null

      const zoned = dayjs.tz(input, timeZone)

      // @ts-ignore
      if (!zoned.isValid()) return input

      const utcDate = zoned.utc()

      return utcDate.format(format)
    },
    [timeZone]
  )

  const getValue = React.useCallback(
    (storedValue?: string, skipTimezone?: boolean): Dayjs | undefined => {
      if (!storedValue) return undefined

      if (skipTimezone) {
        return dayjs(storedValue)
      }

      if (!timeZone) return undefined

      return dayjs(storedValue).tz(timeZone)
    },
    [timeZone]
  )
  const getTimeValue = React.useCallback(
    (storedValue?: string): Dayjs | undefined => {
      if (!storedValue || !timeZone) return undefined
      const today = dayjs().format('YYYY-MM-DD')
      const fullValue = `${today} ${storedValue}`

      // ISO-строка уже содержит offset
      return dayjs(fullValue).tz(timeZone)
    },
    [timeZone]
  )
  const getLabel = React.useCallback(
    (
      storedValue?: string | Date,
      format = 'MM/DD/YYYY hh:mm A',
      skipTimezone?: boolean
    ): string => {
      if (!storedValue) return ''

      if (skipTimezone) {
        return dayjs(storedValue).format(format)
      }

      if (!timeZone) return dayjs.utc(storedValue).format(format)

      return dayjs(storedValue).tz(timeZone).format(format)
    },
    [timeZone]
  )

  const getLabelTime = React.useCallback(
    (storedValue?: string, format = 'YYYY-MM-DD HH:mm z'): string => {
      if (!storedValue || !timeZone) return ''

      const today = dayjs().format('YYYY-MM-DD')
      const fullValue = `${today} ${storedValue}`

      return dayjs(fullValue).tz(timeZone).format(format)
    },
    [timeZone]
  )

  return {
    getForSave,
    getValue,
    getLabel,
    getTimeValue,
    getLabelTime,
  }
}
